import React, { useState, useMemo, useEffect } from 'react';
import { 
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, PieChart, Pie 
} from 'recharts';
import { 
    Loader2, Star, TrendingUp, MessageSquare, ArrowUpDown, Settings, ArrowLeft,
    Users, Building, FileBarChart
} from 'lucide-react';

import api from '../services/api';
import ConfiguracoesAvaliacao from './configuracoes/ConfiguracoesAvaliacao';

const RelatoriosAvaliacoesScreen = ({ currentUser, users = [] }) => {
    const getLocalYYYYMMDD = (d) => {
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    
    // --- ESTADOS DE DADOS ---
    const [startDate, setStartDate] = useState(getLocalYYYYMMDD(firstDay));
    const [endDate, setEndDate] = useState(getLocalYYYYMMDD(today));
    const [selectedAgent, setSelectedAgent] = useState(''); 
    const [reportData, setReportData] = useState([]); // Agora recebe a lista da API
    const [loading, setLoading] = useState(false);
    
    // --- ESTADOS DE UI ---
    const [viewMode, setViewMode] = useState('agents'); 
    const [isConfigMode, setIsConfigMode] = useState(false);
    const [sortDirection, setSortDirection] = useState('desc'); 
    const [clientSortDir, setClientSortDir] = useState('asc'); 

    // Mapa de usuários para fallback
    const userMap = useMemo(() => {
        const map = {};
        users.forEach(u => map[u.id || u._id] = u.name);
        return map;
    }, [users]);

    // Busca dados da API Node
    const fetchReportData = async () => {
        if (!currentUser?.companyId) return;
        setLoading(true);
        try {
            const response = await api.get('/reports/evaluations', {
                params: { startDate, endDate }
            });
            // O backend retorna { metrics: {...}, list: [...] }
            // Vamos usar a 'list' para os gráficos
            setReportData(response.data.list || []);
        } catch (error) { 
            console.error("Erro relatório avaliações:", error); 
            alert("Erro ao buscar dados."); 
        } finally { 
            setLoading(false); 
        }
    };

    // Carrega dados iniciais
    useEffect(() => {
        fetchReportData();
    }, []); // Carrega uma vez ao montar

    // --- CÁLCULOS PRINCIPAIS (Baseado na lista do Mongo) ---
    const metrics = useMemo(() => {
        let totalScore = 0;
        let ratedConversations = 0;
        let promoters = 0; 
        let passives = 0;  
        let detractors = 0;
        
        const agentStats = {};
        const clientStats = {};
        const companyStats = {};

        const distribution = [
            { name: 'Detratores (0-6)', value: 0, color: '#ef4444' },
            { name: 'Neutros (7-8)', value: 0, color: '#eab308' },
            { name: 'Promotores (9-10)', value: 0, color: '#22c55e' }
        ];

        const detailedList = [];

        // Inicializa agentes para mostrar mesmo os sem nota
        users.forEach(u => {
            if (selectedAgent && (u.id !== selectedAgent && u._id !== selectedAgent)) return;
            const uid = u.id || u._id;
            agentStats[uid] = { 
                name: u.name, 
                totalRated: 0, sumScore: 0, npsScore: 0, promoters: 0, detractors: 0
            };
        });

        reportData.forEach(c => {
            // Filtro de Agente no frontend
            if (selectedAgent && c.agentId !== selectedAgent) return;

            const score = (c.rating !== undefined && c.rating !== null) ? parseInt(c.rating) : -1;
            
            if (score >= 0 && score <= 10) {
                ratedConversations++;
                totalScore += score;
                
                if (score >= 9) { promoters++; distribution[2].value++; } 
                else if (score >= 7) { passives++; distribution[1].value++; } 
                else { detractors++; distribution[0].value++; }

                // 1. DADOS POR ATENDENTE
                const userId = c.agentId || 'unknown';
                if (!selectedAgent || userId === selectedAgent) {
                    if (!agentStats[userId]) {
                        agentStats[userId] = { name: c.agent || 'Desconhecido', totalRated: 0, sumScore: 0, promoters: 0, detractors: 0 };
                    }
                    if (agentStats[userId]) {
                        agentStats[userId].totalRated++;
                        agentStats[userId].sumScore += score;
                        if (score >= 9) agentStats[userId].promoters++;
                        if (score <= 6) agentStats[userId].detractors++;
                    }
                }

                // 2. DADOS POR CLIENTE
                const clientName = c.client || 'Desconhecido';
                // O backend já deve mandar o nome, mas usamos o ID se não tiver
                const clientKey = clientName; 

                if (!clientStats[clientKey]) {
                    clientStats[clientKey] = { name: clientName, phone: '', total: 0, sum: 0, lastDate: null };
                }
                clientStats[clientKey].total++;
                clientStats[clientKey].sum += score;
                
                const cDate = c.date ? new Date(c.date) : new Date();
                if (!clientStats[clientKey].lastDate || cDate > clientStats[clientKey].lastDate) {
                    clientStats[clientKey].lastDate = cDate;
                }

                // 3. DADOS POR EMPRESA
                const companyName = c.company || 'Particular / Não Inf.';
                
                if (!companyStats[companyName]) {
                    companyStats[companyName] = { name: companyName, total: 0, sum: 0 };
                }
                companyStats[companyName].total++;
                companyStats[companyName].sum += score;

                // Lista Detalhada
                detailedList.push({
                    id: c.protocol || c.id,
                    date: cDate,
                    clientName: clientName,
                    agentName: c.agent || 'Desconhecido',
                    rating: score,
                    companyName: companyName
                });
            }
        });

        const nps = ratedConversations > 0 ? Math.round(((promoters - detractors) / ratedConversations) * 100) : 0;

        let agentsArray = Object.values(agentStats)
            .filter(a => a.totalRated > 0)
            .map(a => ({
                ...a,
                average: (a.sumScore / a.totalRated).toFixed(1),
                averageNum: (a.sumScore / a.totalRated),
                nps: Math.round(((a.promoters - a.detractors) / a.totalRated) * 100)
            }));

        agentsArray.sort((a, b) => {
            if (sortDirection === 'desc') return b.averageNum - a.averageNum;
            return a.averageNum - b.averageNum;
        });

        let clientsArray = Object.values(clientStats).map(c => ({
            ...c,
            average: (c.sum / c.total).toFixed(1),
            averageNum: (c.sum / c.total)
        }));
        
        clientsArray.sort((a, b) => {
            if (clientSortDir === 'desc') return b.averageNum - a.averageNum; 
            return a.averageNum - b.averageNum; 
        });

        let companiesArray = Object.values(companyStats).map(c => ({
            ...c,
            average: (c.sum / c.total).toFixed(1),
            averageNum: (c.sum / c.total)
        })).sort((a, b) => a.averageNum - b.averageNum); 

        detailedList.sort((a, b) => b.date - a.date); 

        return {
            averageGeneral: ratedConversations > 0 ? (totalScore / ratedConversations).toFixed(1) : '0.0',
            totalRated: ratedConversations,
            npsGeneral: nps,
            agentsArray,
            clientsArray,
            companiesArray,
            distribution,
            detailedList 
        };
    }, [reportData, users, sortDirection, clientSortDir, selectedAgent]);

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className="bg-white p-3 border border-gray-100 shadow-lg rounded-lg">
                    <p className="text-sm font-bold text-gray-800">{label}</p>
                    <p className="text-sm text-blue-600 flex items-center gap-1">Média: {payload[0].value} / 10</p>
                </div>
            );
        }
        return null;
    };

    return (
        <div className="p-8 h-full overflow-y-auto bg-gray-50">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-6 gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-1 flex items-center">
                        <Star className="w-6 h-6 mr-2 text-yellow-500 fill-yellow-500" />
                        Relatório de NPS
                        {isConfigMode && <span className="ml-2 text-gray-400 font-normal text-lg">/ Configurações</span>}
                    </h2>
                    <p className="text-sm text-gray-500">
                        {isConfigMode 
                            ? "Configure a mensagem e ativação da pesquisa." 
                            : "Analise a satisfação por atendente, cliente ou empresa."
                        }
                    </p>
                </div>
                
                <button 
                    onClick={() => setIsConfigMode(!isConfigMode)}
                    className={`flex items-center px-4 py-2 rounded-md shadow-sm border transition-colors ${
                        isConfigMode 
                        ? 'bg-gray-200 text-gray-800 hover:bg-gray-300' 
                        : 'bg-white text-gray-600 hover:text-blue-600 hover:border-blue-300'
                    }`}
                >
                    {isConfigMode ? <><ArrowLeft className="w-4 h-4 mr-2" /> Voltar</> : <><Settings className="w-4 h-4 mr-2" /> Configurar</>}
                </button>
            </div>

            {isConfigMode ? (
                <div className="max-w-4xl mx-auto animate-in fade-in duration-300">
                     <ConfiguracoesAvaliacao companyId={currentUser?.companyId} />
                </div>
            ) : (
                <div className="animate-in fade-in duration-300">
                    <div className="flex flex-col xl:flex-row justify-between items-end gap-4 mb-6">
                        <div className="flex bg-gray-200 p-1 rounded-lg">
                            <button 
                                onClick={() => setViewMode('agents')}
                                className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${viewMode === 'agents' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'}`}
                            >
                                <span className="flex items-center"><FileBarChart className="w-4 h-4 mr-2"/> Por Atendente</span>
                            </button>
                            <button 
                                onClick={() => setViewMode('clients')}
                                className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${viewMode === 'clients' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'}`}
                            >
                                <span className="flex items-center"><Users className="w-4 h-4 mr-2"/> Por Cliente</span>
                            </button>
                            <button 
                                onClick={() => setViewMode('companies')}
                                className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${viewMode === 'companies' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'}`}
                            >
                                <span className="flex items-center"><Building className="w-4 h-4 mr-2"/> Por Empresa</span>
                            </button>
                        </div>

                        <div className="flex flex-wrap items-end gap-2 bg-white p-2 rounded-lg shadow-sm border w-full xl:w-auto">
                            <div className="w-full md:w-auto">
                                <label className="block text-xs text-gray-500 ml-1 font-bold mb-1">Atendente</label>
                                <select value={selectedAgent} onChange={(e) => setSelectedAgent(e.target.value)} className="w-full md:w-40 p-2 border rounded-md text-sm bg-gray-50 focus:ring-2 focus:ring-blue-500 outline-none">
                                    <option value="">Todos</option>
                                    {users.map(u => <option key={u.id || u._id} value={u.id || u._id}>{u.name}</option>)}
                                </select>
                            </div>
                            <div><label className="block text-xs text-gray-500 ml-1">Início</label><input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="p-2 border rounded-md text-sm" /></div>
                            <div><label className="block text-xs text-gray-500 ml-1">Fim</label><input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="p-2 border rounded-md text-sm" /></div>
                            <button onClick={fetchReportData} disabled={loading} className="bg-yellow-500 text-white px-4 py-2 rounded-md hover:bg-yellow-600 disabled:opacity-50 flex items-center h-[38px]">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Filtrar"}</button>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex justify-between items-center">
                            <div><p className="text-sm font-medium text-gray-500">Média Geral</p><h3 className="text-3xl font-bold text-gray-800 mt-1">{metrics.averageGeneral}</h3></div>
                            <div className="p-3 bg-yellow-50 text-yellow-600 rounded-lg"><Star className="w-6 h-6 fill-yellow-600" /></div>
                        </div>
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex justify-between items-center">
                            <div><p className="text-sm font-medium text-gray-500">NPS Score</p><h3 className={`text-3xl font-bold mt-1 ${metrics.npsGeneral >= 75 ? 'text-green-600' : metrics.npsGeneral >= 50 ? 'text-yellow-600' : 'text-red-600'}`}>{metrics.npsGeneral}</h3></div>
                            <div className="p-3 bg-blue-50 text-blue-600 rounded-lg"><TrendingUp className="w-6 h-6" /></div>
                        </div>
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex justify-between items-center">
                            <div><p className="text-sm font-medium text-gray-500">Total Avaliações</p><h3 className="text-3xl font-bold text-gray-800 mt-1">{metrics.totalRated}</h3></div>
                            <div className="p-3 bg-purple-50 text-purple-600 rounded-lg"><MessageSquare className="w-6 h-6" /></div>
                        </div>
                    </div>

                    {viewMode === 'agents' && (
                        <>
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                                <div className="lg:col-span-2 bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                                    <h3 className="text-lg font-bold text-gray-800 mb-6">Média por Atendente</h3>
                                    <div className="h-72 w-full">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart data={metrics.agentsArray} layout="vertical" margin={{ left: 20 }}>
                                                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                                <XAxis type="number" domain={[0, 10]} hide />
                                                <YAxis dataKey="name" type="category" width={100} tick={{fontSize: 12}} />
                                                <Tooltip content={<CustomTooltip />} cursor={{fill: 'transparent'}} />
                                                <Bar dataKey="averageNum" radius={[0, 4, 4, 0]} barSize={24}>
                                                    {metrics.agentsArray.map((entry, index) => (<Cell key={`cell-${index}`} fill={entry.averageNum >= 9 ? '#22c55e' : entry.averageNum >= 7 ? '#eab308' : '#ef4444'} />))}
                                                </Bar>
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                </div>
                                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                                    <h3 className="text-lg font-bold text-gray-800 mb-6">Classificação NPS</h3>
                                    <div className="h-64 w-full relative">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Pie data={metrics.distribution} cx="50%" cy="50%" innerRadius={60} outerRadius={80} paddingAngle={5} dataKey="value">
                                                    {metrics.distribution.map((entry, index) => (<Cell key={`cell-${index}`} fill={entry.color} />))}
                                                </Pie>
                                                <Tooltip />
                                            </PieChart>
                                        </ResponsiveContainer>
                                        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center">
                                            <p className="text-2xl font-bold text-gray-700">{metrics.totalRated}</p><p className="text-xs text-gray-500">Votos</p>
                                        </div>
                                    </div>
                                    <div className="flex justify-center gap-4 mt-2 flex-wrap">
                                        {metrics.distribution.map(d => (<div key={d.name} className="flex items-center gap-1"><div className="w-2 h-2 rounded-full" style={{backgroundColor: d.color}}></div><span className="text-xs text-gray-500">{d.name}</span></div>))}
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mb-8">
                                <div className="p-6 border-b border-gray-100 flex justify-between items-center">
                                    <h3 className="text-lg font-bold text-gray-800">Ranking de Atendentes</h3>
                                    <button onClick={() => setSortDirection(prev => prev === 'desc' ? 'asc' : 'desc')} className="flex items-center text-sm text-gray-500 hover:text-blue-600 transition-colors border px-3 py-1 rounded bg-gray-50"><ArrowUpDown className="w-4 h-4 mr-2" />{sortDirection === 'desc' ? 'Melhor → Pior' : 'Pior → Melhor'}</button>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm text-left">
                                        <thead className="bg-gray-50 text-gray-500 font-medium border-b">
                                            <tr><th className="px-6 py-4">Atendente</th><th className="px-6 py-4 text-center">Média (0-10)</th><th className="px-6 py-4 text-center">NPS</th><th className="px-6 py-4 text-center">Avaliações</th></tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                            {metrics.agentsArray.map((agent) => (
                                                <tr key={agent.name} className="hover:bg-gray-50">
                                                    <td className="px-6 py-4 font-medium text-gray-800">{agent.name}</td>
                                                    <td className="px-6 py-4 text-center"><div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-gray-100 text-gray-800">{agent.average} <Star className="w-3 h-3 ml-1 fill-yellow-500" /></div></td>
                                                    <td className="px-6 py-4 text-center"><span className={`px-2 py-1 rounded text-xs font-bold ${agent.nps >= 75 ? 'bg-green-100 text-green-800' : agent.nps >= 50 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`}>{agent.nps}</span></td>
                                                    <td className="px-6 py-4 text-center text-gray-600">{agent.totalRated}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </>
                    )}

                    {viewMode === 'clients' && (
                        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mb-8 animate-in fade-in slide-in-from-bottom-2">
                            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-blue-50">
                                <div>
                                    <h3 className="text-lg font-bold text-blue-900">Satisfação por Cliente</h3>
                                    <p className="text-xs text-blue-600">Média de notas dadas por cada usuário.</p>
                                </div>
                                <button onClick={() => setClientSortDir(prev => prev === 'desc' ? 'asc' : 'desc')} className="flex items-center text-sm text-blue-700 bg-white hover:bg-blue-100 transition-colors border border-blue-200 px-3 py-1 rounded shadow-sm">
                                    <ArrowUpDown className="w-4 h-4 mr-2" />
                                    {clientSortDir === 'desc' ? 'Melhores → Piores' : 'Piores → Melhores'}
                                </button>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm text-left">
                                    <thead className="bg-gray-50 text-gray-500 font-medium border-b">
                                        <tr><th className="px-6 py-4">Cliente</th><th className="px-6 py-4 text-center">Média de Nota</th><th className="px-6 py-4 text-center">Qtd. Avaliações</th><th className="px-6 py-4 text-right">Última Avaliação</th></tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {metrics.clientsArray.length > 0 ? metrics.clientsArray.map((client) => (
                                            <tr key={client.name} className="hover:bg-gray-50">
                                                <td className="px-6 py-4 font-medium text-gray-800">{client.name}</td>
                                                <td className="px-6 py-4 text-center">
                                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-bold ${client.averageNum < 7 ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                                                        {client.average}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-center text-gray-600">{client.total}</td>
                                                <td className="px-6 py-4 text-right text-gray-500 text-xs">{client.lastDate ? client.lastDate.toLocaleDateString('pt-BR') : '-'}</td>
                                            </tr>
                                        )) : (<tr><td colSpan="5" className="px-6 py-8 text-center text-gray-400">Nenhum dado encontrado.</td></tr>)}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {viewMode === 'companies' && (
                        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mb-8 animate-in fade-in slide-in-from-bottom-2">
                            <div className="p-6 border-b border-gray-100 bg-purple-50">
                                <h3 className="text-lg font-bold text-purple-900">Satisfação por Empresa</h3>
                                <p className="text-xs text-purple-600">Agrupamento de notas por empresa cadastrada no contato.</p>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm text-left">
                                    <thead className="bg-gray-50 text-gray-500 font-medium border-b">
                                        <tr><th className="px-6 py-4">Empresa</th><th className="px-6 py-4 text-center">Média Geral</th><th className="px-6 py-4 text-center">Status</th><th className="px-6 py-4 text-center">Total de Votos</th></tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {metrics.companiesArray.length > 0 ? metrics.companiesArray.map((comp) => (
                                            <tr key={comp.name} className="hover:bg-gray-50">
                                                <td className="px-6 py-4 font-bold text-gray-700 uppercase tracking-wide text-xs">{comp.name}</td>
                                                <td className="px-6 py-4 text-center">
                                                    <div className="flex items-center justify-center gap-2">
                                                        <span className="text-lg font-bold text-gray-800">{comp.average}</span>
                                                        <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                                                            <div className={`h-full ${comp.averageNum >= 9 ? 'bg-green-500' : comp.averageNum >= 7 ? 'bg-yellow-500' : 'bg-red-500'}`} style={{width: `${(comp.averageNum/10)*100}%`}}></div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-center">
                                                    <span className={`px-2 py-1 rounded text-xs font-bold uppercase ${comp.averageNum >= 9 ? 'bg-green-100 text-green-700' : comp.averageNum >= 7 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>
                                                        {comp.averageNum >= 9 ? 'Promotor' : comp.averageNum >= 7 ? 'Neutro' : 'Detrator'}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-center font-mono text-gray-600">{comp.total}</td>
                                            </tr>
                                        )) : (<tr><td colSpan="4" className="px-6 py-8 text-center text-gray-400">Nenhuma empresa identificada nos contatos.</td></tr>)}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mt-8">
                        <div className="p-4 bg-gray-50 border-b border-gray-200">
                            <h4 className="text-sm font-bold text-gray-600 uppercase">Histórico Recente de Notas</h4>
                        </div>
                        <div className="max-h-60 overflow-y-auto">
                            <table className="w-full text-xs text-left">
                                <tbody className="divide-y divide-gray-100">
                                    {metrics.detailedList.slice(0, 50).map((item, index) => (
                                        <tr key={index} className="hover:bg-gray-50">
                                            <td className="px-4 py-2 text-gray-500">{item.date.toLocaleDateString()}</td>
                                            <td className="px-4 py-2 font-medium">{item.clientName} <span className="text-gray-400 font-normal">({item.companyName})</span></td>
                                            <td className="px-4 py-2 text-gray-500">Atendido por {item.agentName}</td>
                                            <td className="px-4 py-2 text-right font-bold text-gray-800"><span className="bg-gray-100 px-1 rounded">{item.rating}</span></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                </div>
            )}
        </div>
    );
};

export default RelatoriosAvaliacoesScreen;